import { Icon } from '@iconify/react/dist/iconify.js';
import { Box, Checkbox, FormControl, FormHelperText, InputAdornment, MenuItem, Select, TextField } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';

const NO_RESULTS_VALUE = '__no_results__';

export default function CustomSelectMultiple2({
    values, onChange, error, data, title, required, disabled, placeholder, subtitle, name, valueName, labelName, width, module, searchable, searchPlaceholder
}) {
    const [all, setAll] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [open, setOpen] = useState(false);
    const searchInputRef = useRef(null);

    const getItemLabel = (item) => item?.[`${labelName}`] || item?.name || item?.label || '';

    const filteredData = useMemo(() => {
        if (!searchable || !searchText.trim()) {
            return data || [];
        }

        const query = searchText.trim().toLowerCase();
        return (data || []).filter((item) => getItemLabel(item).toLowerCase().includes(query));
    }, [data, labelName, searchText, searchable]);

    const handleClose = () => {
        setOpen(false);
        setSearchText('');
    };

    useEffect(() => {
        if (!open || !searchable) {
            return undefined;
        }

        const timer = window.setTimeout(() => {
            searchInputRef.current?.focus();
        }, 50);

        return () => window.clearTimeout(timer);
    }, [open, searchable]);

    useEffect(() => {
        const key = valueName || 'value';
        const allValues = (data || []).map(item => item[key]);
        const allSelected = allValues.length > 0 && values?.length === allValues.length;
        setAll(allSelected);
    }, [values, data, valueName]);

    const handleOnChange = (event) => {
        let selected = (event.target.value || []).filter((value) => value !== NO_RESULTS_VALUE);

        const key = valueName || 'value';
        const allValues = (data || []).map(item => item[key]);
        const isNumber = allValues.length > 0 && typeof allValues[0] === 'number';

        const includesAll = selected.includes('all');
        if (includesAll) {
            if ((values || []).length !== allValues.length) {
                onChange(allValues);
            } else {
                onChange([]);
            }
            return;
        }

        const normalized = selected.map(v => (isNumber ? Number(v) : v));
        onChange(normalized);
    };

    const searchField = (
        <Box
            component="div"
            sx={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                bgcolor: '#fff',
                py: 1,
                px: 1.5,
                borderBottom: '1px solid #eee',
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
        >
            <TextField
                inputRef={searchInputRef}
                size="small"
                fullWidth
                placeholder={searchPlaceholder || 'Search...'}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <i className="ri-search-line" style={{ color: '#74788D', fontSize: 16 }} />
                        </InputAdornment>
                    ),
                }}
            />
        </Box>
    );

    return (
        <>
            <FormControl variant="outlined" fullWidth>
                <Select
                    size="small"
                    value={values}
                    name={name}
                    required={required}
                    disabled={disabled}
                    multiple
                    displayEmpty
                    disableUnderline
                    open={searchable ? open : undefined}
                    onOpen={searchable ? () => setOpen(true) : undefined}
                    onClose={searchable ? handleClose : undefined}
                    placeholder={placeholder || 'Choose'}
                    onChange={handleOnChange}
                    MenuProps={searchable ? {
                        autoFocus: false,
                        disableAutoFocusItem: true,
                        PaperProps: { style: { maxHeight: 360 } },
                        MenuListProps: {
                            subheader: searchField,
                        },
                    } : undefined}
                    style={{ color: (values === "" || values === "-1") && "#c8c8c8", height: 33, backgroundColor: 'white', borderRadius: 15, padding: 0, }}
                    sx={{
                        '& .MuiFilledInput-input': {
                            borderRadius: 10,
                            backgroundColor: 'white',
                            padding: 0,
                            paddingInline: 2
                        },
                    }}
                    IconComponent={() => (
                        <i className="ri-expand-up-down-line mx-2" style={{ zIndex: 2 }} />
                    )}
                    renderValue={() => (
                        <Box sx={{ display: 'flex', gap: 0.5, marginLeft: 1 }} onClick={(e) => e.stopPropagation()}>
                            {values?.length > 0 ? `${values?.length} selected` : (
                                <span>
                                    {placeholder}
                                </span>
                            )}
                        </Box>
                    )}
                >
                    <MenuItem key={'all'} value={'all'}>
                        <Checkbox
                            checked={all}
                            sx={{
                                '&.Mui-checked': { color: "#ED1A3D" },
                                '& .MuiSvgIcon-root': { fontSize: 18 },
                            }}
                        />
                        All {module || 'items'}
                    </MenuItem>
                    {
                        filteredData.length > 0 ? filteredData.map((x) => {
                            const itemValue = x[`${valueName}`] || x?.id || x?.value;
                            return (
                                <MenuItem
                                    className='d-flex align-items-center'
                                    key={itemValue}
                                    value={itemValue}
                                    style={{ backgroundColor: (x?.value || x?.id) === values ? '#FEC00A' : 'white' }}
                                >
                                    <Checkbox
                                        checked={values?.some(v => String(v) === String(itemValue)) || false}
                                        sx={{
                                            '&.Mui-checked': { color: "#ED1A3D" },
                                            '& .MuiSvgIcon-root': { fontSize: 18 },
                                        }}
                                    />
                                    {getItemLabel(x)}
                                </MenuItem>
                            );
                        }) : (
                            <MenuItem disabled value={NO_RESULTS_VALUE}>
                                No results found
                            </MenuItem>
                        )
                    }
                </Select>
                {subtitle && <span style={{ fontSize: 13, color: '#74788D', marginTop: '5px', fontWeight: '300' }}>{subtitle}</span>}
            </FormControl >
            {error && <FormHelperText sx={{ color: "#BA2727" }}><Icon icon={'fluent:error-circle-24-filled'} height={16} width={16} />{error || ""}</FormHelperText>
            }

        </>
    );
}
